import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/Topbar";
import PasswordInput from "@/components/PasswordInput";
import { ArrowRight, Lightning, Briefcase, HardHat } from "@phosphor-icons/react";

export default function Register() {
  const { register, user, error } = useAuth();
  const [role, setRole] = useState(""); // "" | "pe" | "client"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!role) return;
    setBusy(true);
    const ok = await register(email, password, name, role);
    setBusy(false);
    if (ok) nav("/dashboard");
  };

  return (
    <div className="industrial min-h-screen bg-[#0A0C10] flex flex-col">
      <Topbar />
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        <div className="flex items-center justify-center p-8 md:p-12 order-2 lg:order-1">
          <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
            <div className="label-tag">// New account</div>
            <h1 className="font-display text-3xl font-black mt-2">Join the platform</h1>
            <p className="text-sm text-gray-400 mt-2">
              {role === "pe" && "Vetted network of licensed engineers. Admin approves every profile before it goes live."}
              {role === "client" && "Post a project, review proposals from licensed engineers, and collaborate."}
              {!role && "Pick your account type to get started."}
            </p>

            {error && (
              <div className="mt-6 p-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm font-mono" data-testid="register-error">
                {error}
              </div>
            )}

            {/* Role picker — required before the form is submissible */}
            <div className="mt-6 grid grid-cols-2 gap-3" data-testid="register-role-picker">
              <button
                type="button"
                onClick={() => setRole("pe")}
                className={`border p-4 text-left transition ${role === "pe" ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-white/10 hover:border-white/30"}`}
                data-testid="register-role-pe"
              >
                <HardHat size={24} weight="duotone" className={role === "pe" ? "text-[#00E5FF]" : "text-gray-400"} />
                <div className="mt-2 font-display font-bold text-sm">Professional Engineer</div>
                <div className="text-[10px] text-gray-400 mt-1 font-mono">Offer services · Manage projects</div>
              </button>
              <button
                type="button"
                onClick={() => setRole("client")}
                className={`border p-4 text-left transition ${role === "client" ? "border-[#00E5FF] bg-[#00E5FF]/10" : "border-white/10 hover:border-white/30"}`}
                data-testid="register-role-client"
              >
                <Briefcase size={24} weight="duotone" className={role === "client" ? "text-[#00E5FF]" : "text-gray-400"} />
                <div className="mt-2 font-display font-bold text-sm">Client</div>
                <div className="text-[10px] text-gray-400 mt-1 font-mono">Post jobs · Hire engineers</div>
              </button>
            </div>

            <label className="block mt-6">
              <span className="label-tag">Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full bg-black/40 border border-white/10 px-3 py-3 text-white font-mono text-sm focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
                data-testid="register-name-input"
                placeholder={role === "pe" ? "Jane Engineer, P.E." : "Company or your name"}
              />
            </label>

            <label className="block mt-4">
              <span className="label-tag">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-black/40 border border-white/10 px-3 py-3 text-white font-mono text-sm focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
                data-testid="register-email-input"
                placeholder="you@example.com"
              />
            </label>

            <label className="block mt-4">
              <span className="label-tag">Password (min 10 chars, letter + digit + special)</span>
              <div className="mt-2">
                <PasswordInput
                  required
                  minLength={10}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  data-testid="register-password-input"
                  placeholder="••••••••••"
                  showStrength
                />
              </div>
            </label>

            <button
              type="submit"
              disabled={busy || !role}
              className="btn-primary w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="register-submit"
              title={!role ? "Pick an account type first" : ""}
            >
              {busy ? "Creating…" : (<>Create account <ArrowRight size={18} weight="bold" /></>)}
            </button>

            <p className="mt-6 text-sm text-gray-400">
              Already have an account?{" "}
              <Link to="/login" className="text-[#00E5FF] hover:underline" data-testid="link-login">
                Sign in
              </Link>
            </p>
          </form>
        </div>

        <div className="hidden lg:flex relative bp-grid border-l border-white/10 order-1 lg:order-2">
          <div className="absolute inset-0 bg-gradient-to-bl from-[#00E5FF]/10 via-transparent to-transparent" />
          <div className="relative m-auto p-12 max-w-md text-right">
            <Lightning size={36} weight="fill" className="text-[#00E5FF] ml-auto" />
            <h2 className="mt-6 font-display text-4xl font-black tracking-tighter">Where clients meet licensed engineers.</h2>
            <p className="mt-4 text-gray-400">Post a project. Review proposals. Collaborate — end-to-end, on one platform.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
